import { defineNuxtPlugin, useCookie, useRuntimeConfig } from 'nuxt/app';
import { watch } from 'vue';

export type FetchingResponse<G> = {
  status?: number;
  statusText?: string;
  data?: G | null;
};

export enum FetchingServices {
  crm = 'crm',
  product = 'product',
  ipbx = 'ipbx',
  webchat = 'webchat',
  webchat2 = 'webchat2',
  whatsapp = 'whatsapp',
  socket = 'socket',
  sip = 'sip',
  cdr = 'cdr',
  wallboard = 'wallboard',
  money = 'money',
  ci = 'ci',
  cibiz = 'cibiz',
  qaqc = 'qaqc',
  fax = 'fax',
  conference = 'conference',
  conferencelink = 'conferencelink',
  msg = 'msg',
  cellar = 'cellar',
  uaa = 'uaa',
  ticketing = 'ticketing',
  ticket = 'ticket',
  push = 'push',
  status = 'status',
  inventory = 'inventory',
  lead = 'lead',
  invoice = 'invoice',
  sms = 'sms',
  task = 'task',
  deal = 'deal',
  uuid = 'uuid',
  mothership = 'mothership',
  webchatcdn = 'webchatcdn',
  domain = 'domain',
  timeline = 'timeline',
  automation = 'automation',
}

export enum FetchingMethods {
  get = 'GET',
  post = 'POST',
  put = 'PUT',
  delete = 'DELETE',
  patch = 'PATCH',
}

export class Fetching {
  method?: FetchingMethods;
  service?: FetchingServices;
  url?: string;
  body?: object | null;
  withToken?: boolean;
  timeout?: number;
  responseType?: string | null;
  private services?: Map<string, string> | null;
  private accessToken?: string | null;

  constructor(
    method?: FetchingMethods,
    service?: FetchingServices,
    url?: string,
    body: object | null = null,
    withToken: boolean = true,
    timeout: number = 0,
    responseType: string | null = null,
    services: Map<string, string> | null = null,
    accessToken: string | null = null
  ) {
    this.method = method;
    this.service = service;
    this.withToken = withToken;
    this.url = url;
    this.body = body;
    this.timeout = timeout;
    this.responseType = responseType;
    this.services = services;
    this.accessToken = accessToken;
  }

  private async send<G>(
    method: FetchingMethods,
    service: FetchingServices,
    url: string,
    body: object | null = null,
    withToken: boolean = true,
    timeout: number = 0,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    let resp: FetchingResponse<G> = {};
    try {
      if (
        ![
          FetchingMethods.get,
          FetchingMethods.post,
          FetchingMethods.put,
          FetchingMethods.delete,
          FetchingMethods.patch,
        ].includes(<FetchingMethods>method)
      ) {
        resp.status = 405;
        resp.statusText = 'Method Not Allowed!';
        resp.data = null;
      } else {
        const options = {
          method,
          headers: {
            'x-api-method': method,
            'x-api-service': service,
            'x-api-url': url,
            'x-api-with': String(withToken),
            'x-api-timeout': String(timeout),
          },
          // body: null,
          // timeout: null,
          // signal: null,
          // responseType: null
        };

        /// Implement with fetch
        /*
        if (body) {
          Object.assign(options, {
            body: JSON.stringify(body)
          });
        }
        if (timeout > 0) {
          const _controller = new AbortController();
          const _timeout = setTimeout(() => {
            _controller.abort();
          }, timeout);

          Object.assign(options, {
            timeout: _timeout,
            signal: _controller.signal,
          });
        }

        let _data: object | null = null;
        await fetch(`/api/`, options)
          .then((response) => {
            resp.status = response?.status;
            resp.statusText = response.statusText; //!response?.ok ? "fetching; response is wrong!" : "OK";
            return response.json();
          })
          .then((data) => {
            _data = data;
          })
          .catch((error) => {
            if (timeout > 0 && options?.timeout) clearTimeout(options.timeout);
            resp.status = error?.statusCode;
            resp.statusText = error?.data;
          });
        resp.data = _data;
        */

        /// Implement with $fetch
        if (body) {
          const isFormData = body instanceof FormData;
          Object.assign(options, {
            body: isFormData ? body : JSON.stringify(body),
          });
        }
        if (timeout > 0) {
          Object.assign(options, {
            timeout: timeout,
          });
        }
        if (responseType) {
          Object.assign(options, {
            'response-type': responseType,
          });
        }
        try {
          let res: any | unknown;
          if (responseType == 'blob') {
            res = await $fetch(`${this.services?.get(service)}/${url}`, {
              method: options.method,
              responseType: responseType,
              headers: {
                Authorization: `Bearer ${this.accessToken}`,
              },
            });
          } else {
            res = await $fetch<any>('/api/v2/' + url, options);
          }

          // Default normalization
          resp.status = 200;
          resp.statusText = 'OK';
          resp.data = null;

          if (res == null || res === undefined) {
            // Case 1: No content (204 or null response)
            resp.status = 200;
            resp.statusText = 'No Content';
            resp.data = null;
          } else if (Array.isArray(res)) {
            // Case 2: Raw array
            resp.data = res as G;
          } else if (typeof res === 'object') {
            if ('data' in res && ('status' in res || 'message' in res)) {
              // Case 3: Full structured response { data, status, message }
              resp.data = res.data ?? null;
              resp.status = res.status ?? 200;
              resp.statusText = res.message ?? 'OK';
            } else if ('data' in res) {
              // Case 4: Only { data }
              resp.data = res.data ?? null;
            } else {
              // Case 5: Plain object (treat whole thing as data)
              resp.data = res;
            }
          } else {
            // Case 6: Primitive (string, number, boolean, etc.)
            resp.data = res;
          }
        } catch (err: any) {
          // handle timeout error
          if (err.cause?.code === 23) {
            resp.status = 408;
            resp.statusText = 'Request Timeout';
            resp.data = null;
          } else {
            resp.status = err?.statusCode ?? 500;
            resp.statusText = err?.data ?? 'Unknown Error';
            resp.data = null;
            // Invoice System Exception Handler
            if (err?.data?.data?.status >= 1000) {
              resp.status = err?.data?.data?.status;
              resp.statusText = err?.data?.data?.message;
              resp.data = null;
            }
          }
        }
      }
    } catch (error: any) {
      resp.status = 500;
      resp.statusText = error instanceof Error ? error.message : <string>error;
      resp.data = null;
    }
    return resp;
  }

  public set(
    services: Map<string, string> | null = null,
    accessToken: string | null = null
  ) {
    this.services = services;
    this.accessToken = accessToken;
  }

  public async get<G>(
    service: FetchingServices,
    url: string,
    withToken?: boolean,
    timeout?: number,
    responseType?: string | null
  ): Promise<FetchingResponse<G>>;
  public async get<G>(): Promise<FetchingResponse<G>>;
  public async get<G>(
    service?: FetchingServices,
    url?: string,
    withToken: boolean = true,
    timeout: number = 0,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    if (service && url)
      return this.send<G>(
        FetchingMethods.get,
        service,
        url,
        null,
        withToken,
        timeout,
        responseType
      );
    else
      return this.send<G>(
        FetchingMethods.get,
        <FetchingServices>this.service,
        <string>this.url,
        null,
        withToken,
        <number>this.timeout ?? 0,
        responseType
      );
  }

  public async post<G>(
    service: FetchingServices,
    url: string,
    body?: object | null,
    withToken?: boolean,
    timeout?: number,
    responseType?: string | null
  ): Promise<FetchingResponse<G>>;
  public async post<G>(): Promise<FetchingResponse<G>>;
  public async post<G>(
    service?: FetchingServices,
    url?: string,
    body: object | null = null,
    withToken: boolean = true,
    timeout: number = 0,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    if (service && url)
      return this.send<G>(
        FetchingMethods.post,
        service,
        url,
        body,
        withToken,
        timeout,
        responseType
      );
    else
      return this.send<G>(
        FetchingMethods.post,
        <FetchingServices>this.service,
        <string>this.url,
        body,
        withToken,
        <number>this.timeout,
        responseType
      );
  }

  public async put<G>(
    service: FetchingServices,
    url: string,
    body?: object | null,
    withToken?: boolean,
    timeout?: number,
    responseType?: string | null
  ): Promise<FetchingResponse<G>>;
  public async put<G>(): Promise<FetchingResponse<G>>;
  public async put<G>(
    service?: FetchingServices,
    url?: string,
    body: object | null = null,
    withToken: boolean = true,
    timeout: number = 0,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    if (service && url)
      return this.send<G>(
        FetchingMethods.put,
        service,
        url,
        body,
        withToken,
        timeout,
        responseType
      );
    else
      return this.send<G>(
        FetchingMethods.put,
        <FetchingServices>this.service,
        <string>this.url,
        body,
        withToken,
        <number>this.timeout,
        responseType
      );
  }

  public async delete<G>(
    service: FetchingServices,
    url: string,
    body?: object | null,
    withToken?: boolean,
    timeout?: number,
    responseType?: string | null
  ): Promise<FetchingResponse<G>>;
  public async delete<G>(): Promise<FetchingResponse<G>>;
  public async delete<G>(
    service?: FetchingServices,
    url?: string,
    body: object | null = null,
    withToken: boolean = true,
    timeout: number = 0,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    if (service && url)
      return this.send<G>(
        FetchingMethods.delete,
        service,
        url,
        body,
        withToken,
        timeout,
        responseType
      );
    else
      return this.send<G>(
        FetchingMethods.delete,
        <FetchingServices>this.service,
        <string>this.url,
        body,
        withToken,
        <number>this.timeout,
        responseType
      );
  }

  public async patch<G>(
    service: FetchingServices,
    url: string,
    body?: object | null,
    withToken?: boolean,
    timeout?: number,
    responseType?: string | null
  ): Promise<FetchingResponse<G>>;
  public async patch<G>(): Promise<FetchingResponse<G>>;
  public async patch<G>(
    service?: FetchingServices,
    url?: string,
    body: object | null = null,
    withToken: boolean = true,
    timeout: number = 0,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    if (service && url)
      return this.send<G>(
        FetchingMethods.patch,
        service,
        url,
        body,
        withToken,
        timeout,
        responseType
      );
    else
      return this.send<G>(
        FetchingMethods.patch,
        <FetchingServices>this.service,
        <string>this.url,
        body,
        withToken,
        <number>this.timeout,
        responseType
      );
  }
}
// export const fetching = new Fetching();

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const accessToken = useCookie('accessToken');

  const services = new Map<string, string>([
    ['crm', config.public.CRM_URL as string],
    ['product', config.public.PRODUCT_URL as string],
    ['ipbx', config.public.IPBX_URL as string],
    ['webchat', config.public.WEBCHAT_URL as string],
    ['webchat2', config.public.WEBCHAT_URL2 as string],
    ['whatsapp', config.public.WHATSAPP_URL as string],
    ['socket', config.public.SOCKET_URL as string],
    ['sip', config.public.SIP_URL as string],
    ['cdr', config.public.CDR_URL as string],
    ['wallboard', config.public.WALLBOARD_URL as string],
    ['money', config.public.MONEY_DRAGON_URL as string],
    ['oto', config.public.OTO_URL as string],
    ['ci', config.public.CI_URL as string],
    ['cibiz', config.public.CI_BIZ_URL as string],
    ['qaqc', config.public.QAQC_URL as string],
    ['fax', config.public.FAX_URL as string],
    ['conference', config.public.VIDEO_CONFERENCE_URL as string],
    ['conferencelink', config.public.VIDEO_CONFERENCE_LINK as string],
    ['msg', config.public.MSG_GORETRIEVAL_URL as string],
    ['cellar', config.public.CELLAR_URL as string],
    ['uaa', config.public.UAA_DRAGON_URL as string],
    ['ticketing', config.public.TICKETING_URL as string],
    ['ticket', config.public.TICKET_URL as string],
    ['push', config.public.PUSH_BIZ_URL as string],
    ['status', config.public.STATUS_URL as string],
    ['inventory', config.public.INVENTORY_URL as string],
    ['lead', config.public.LEAD_URL as string],
    ['invoice', config.public.INVOICE_URL as string],
    ['sms', config.public.SMS_URL as string],
    ['task', config.public.TASK_URL as string],
    ['deal', config.public.DEAL_URL as string],
    ['uuid', config.public.HOSTED_UUID as string],
    ['mothership', config.public.BASE_MOTHERSHIP_DOMAIN as string],
    ['webchatcdn', config.public.WEBCHAT_CDN as string],
    ['domain', config.public.DOMAIN as string],
    ['automation', config.public.AUTOMATION as string],
    ['timeline', config.public.TIMELINE_URL as string],
  ]);

  const fetching = new Fetching();
  fetching.set(services, accessToken.value);
  watch(
    accessToken,
    async (token: string | null | undefined) => {
      if (token) {
        fetching.set(services, accessToken.value);
      }
    },
    { immediate: true }
  );

  /**************
   * nuxtApp.provide('fetching', fetching);
   * using:
   * useNuxtApp().$fetching.get(FetchingServices.uaa, 'info');
   */
  nuxtApp.provide('fetching', fetching);

  /**************
   * globalThis.fetching = fetching;
   * using:
   * fetching.post(FetchingServices.uaa, 'info');
   */

  globalThis.fetching = fetching;

  nuxtApp.provide('fetchingServices', FetchingServices);
});
